import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { setLanguage } from '../utils/language';
import { hasRole } from '../utils/roles';
import HelpPanel from './HelpPanel';

// key가 있는 항목은 "시스템 설정 > 메뉴 권한" 화면에서 목회자/리더 노출 여부를 관리자가 직접 설정할 수 있음
// (서버 /api/menu-permissions 에서 값을 받아와 roles 대신 이 값을 우선 사용).
// key가 없는 항목(시스템 설정/사용자 관리)은 항상 admin 전용으로 코드에 고정 — 권한 상승 방지.
// 아래 순서/그룹은 사용자(관리자)가 직접 정리해준 분류를 그대로 반영한 것:
//   교회 — 교회연혁/행사·일정/사진아카이브
//   교인 — 교인관리/출석체크/어린이체크인/카드인식/목회관리/등록신청/등록QR코드
//   조직 — 주일학교/구역
//   회계 — 헌금/재정
//   설정 — 사용자관리/시스템설정 (항상 맨 아래, admin 전용)
// group/groupEn은 사이드바에 소제목으로 표시되는 값 (collapsed 상태에서는 표시 안 함).
const NAV_ITEMS = [
  { key:'church_history',ko:'교회 연혁',    en:'Church History', path:'/church-history', icon:'ti-history',        group:'교회', groupEn:'Church' },
  { key:'events',        ko:'행사/일정',    en:'Events',        path:'/events',         icon:'ti-calendar-event', group:'교회', groupEn:'Church' },
  { key:'photos',        ko:'사진 아카이브', en:'Photos',        path:'/photos',         icon:'ti-photo',          group:'교회', groupEn:'Church' },
  // 미팅룸 예약: 2026-10 신설 — 그동안 목사님 한 분이 전화로 요청을 받아 수동으로
  // 방을 배정해주던 것을 대체. key가 있어 menu_permissions(MENU_KEYS에 'meeting_rooms'
  // 추가됨)로 pastor/leader 노출 여부를 관리자가 설정할 수 있고, roles를 따로
  // 지정하지 않아 isMenuVisible의 기본 분기(재정 진영 전체는 이미 위에서 막힘)를 탄다.
  { key:'meeting_rooms', ko:'미팅룸 예약',   en:'Meeting Rooms', path:'/meeting-rooms',  icon:'ti-door',           group:'교회', groupEn:'Church' },

  { key:'members',       ko:'교인 관리',    en:'Members',       path:'/members',        icon:'ti-user',           group:'교인', groupEn:'Members' },
  { key:'pastoral',      ko:'목회 관리',    en:'Pastoral',      path:'/pastoral',       icon:'ti-heart',          group:'교인', groupEn:'Members', roles:['pastoral_admin','pastor'] },
  // 중보기도 릴레이는 더 이상 별도 사이드바 메뉴가 아니다 — "행사/일정" 화면의 하위
  // 기능으로 옮겨서 그 화면의 "+ 중보기도 릴레이" 버튼으로 진입한다(Events.jsx 참고).
  // 라우트(/prayer-relay)와 menu_permissions 키('prayer_relay')는 그 버튼의 노출 여부를
  // 판단하는 데 그대로 재사용하므로 남겨둔다 — 여기서 지우는 건 사이드바 항목뿐이다.
  { key:'attendance',    ko:'출석 체크',    en:'Attendance',    path:'/attendance',     icon:'ti-calendar-check', group:'교인', groupEn:'Members' },
  { key:'checkin',       ko:'어린이 체크인', en:'Kids Check-in', path:'/checkin',        icon:'ti-mood-kid',       group:'교인', groupEn:'Members' },
  { key:'card_scan',     ko:'카드 인식',    en:'Card Scan',     path:'/card-scan',      icon:'ti-camera',         group:'교인', groupEn:'Members' },
  { key:'registrations', ko:'등록 신청',    en:'Registrations', path:'/registrations',  icon:'ti-clipboard-list', group:'교인', groupEn:'Members', roles:['pastoral_admin','pastor'] },
  { key:'qrcode',        ko:'등록 QR코드',  en:'QR Code',       path:'/qrcode',         icon:'ti-qrcode',         group:'교인', groupEn:'Members' },

  { key:'groups',        ko:'주일학교/구역', en:'Groups',        path:'/groups',         icon:'ti-users',          group:'조직', groupEn:'Organization' },

  // 회의록: 사역/재정 관리자 둘 다 접근 가능하지만, 실제로 보이는 내용은 각자 자기
  // 진영(사역 vs 재정) 것만 — 서버(meetingMinutesController.js의 domainOf)에서 강제
  // 분리하므로 프론트는 메뉴 노출만 신경 쓰면 된다. 헌금/재정과 성격이 다르므로
  // '회계' 그룹에 억지로 끼워 넣지 않고 별도 그룹으로 둔다.
  // 2026-09: 안수집사(deacon)/제직(jikje)도 자기 권한 범위 안에서 접근 가능 —
  // roles는 관리자 두 역할만 남겨두고, deacon/jikje는 isMenuVisible의 별도 예외
  // 처리로 노출한다(그 역할은 애초에 다른 항목엔 전혀 roles가 없어서 이 목록에
  // 의존하지 않음). 'finance'(헌금/재정 담당) 역할은 회의록에 접근할 수 없다(서버
  // routes/meetingMinutes.js도 동일 — deacon/jikje만 추가로 허용).
  { key:'meetingMinutes', ko:'회의록', en:'Meeting Minutes', path:'/meeting-minutes', icon:'ti-notebook', group:'회의', groupEn:'Meetings', roles:['pastoral_admin','deacon_admin','jikje_admin'] },

  // 제직회: 임원진(목회자/사역 관리자/재정 관리자) + 안수집사(deacon)/제직(jikje, 위의
  // isMenuVisible 예외 처리로 노출)만 — 2026-09 신설, 2026-09 제직 추가. 그 아래
  // 부서(관리부/선교부 등)는 사이드바가 아니라 Committee.jsx 화면 안의 탭으로 보여주고,
  // 그 안의 자료(비품/선교 프로젝트)는 여기 접근 가능한 사람이면 누구나 자유롭게
  // 올리고 수정·삭제할 수 있다(부서별 세부 권한을 따로 두지 않음).
  { key:'committee', ko:'제직회', en:'Committee', path:'/committee', icon:'ti-briefcase', group:'회의', groupEn:'Meetings', roles:['pastor','pastoral_admin','deacon_admin','jikje_admin','deacon','jikje'] },

  // 헌금/재정 메뉴는 사역(pastoral) 진영 전체 — 사역 관리자(pastoral_admin) 포함 —
  // 에게는 절대 보이지 않는다(사역-재정 완전 분리 원칙: 사역 관리자는 결국 담임목사님
  // 계정이므로, 사역 관리자에게 열어주면 "목사님은 재정을 볼 수 없다"는 목표 자체가
  // 무너진다). 재정 관리자(deacon_admin)·헌금/재정 담당(finance)·안수집사(deacon,
  // 조회전용)만 접근 가능 — roles는 비워두고 isMenuVisible의 별도 예외 처리로만 판단한다.
  { key:'finance',       ko:'헌금/재정',    en:'Finance',       path:'/finance',        icon:'ti-coin',           group:'회계', groupEn:'Finance', roles:[] },

  // 2026-09: 사용자 관리는 pastoral_admin/deacon_admin/jikje_admin 모두 접근 가능(각자
  // 자기 진영 계정만 관리 — usersController.js/utils/adminGuard.js). jikje_admin은
  // deacon_admin과 완전히 동등한 재정 진영 관리자다. 시스템 설정은 pastoral_admin 전용.
  { ko:'사용자 관리',  en:'Users',         path:'/settings/users', icon:'ti-users-group',  group:'설정', groupEn:'Settings', roles:['pastoral_admin','deacon_admin','jikje_admin'] },
  { ko:'시스템 설정',  en:'Settings',      path:'/settings/codes', icon:'ti-settings',     group:'설정', groupEn:'Settings', roles:['pastoral_admin'] },
];

const COLLAPSE_KEY = 'halak_sidebar_collapsed';

// 사진 아카이브는 Enterprise 플랜 교회만 사용 가능(2026-09 확정, 실제 접근 제한은
// 서버 /api/photos 라우트의 requirePlan 미들웨어가 담당). 여기서는 다른 플랜 교회에도
// 메뉴 자체는 보여주되(존재를 알려서 업그레이드 유도) 흐리게 표시하고 클릭이 안 되게만 한다.
const PLAN_GATED_MENU_KEYS = { photos: ['enterprise'] };

export default function Layout({ children }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { i18n } = useTranslation();
  const lang      = i18n.language;

  // 2026-10: 좁은 화면(폰)에서 처음 접속하면 — 아직 이 기기/브라우저에서 한 번도
  // 접기/펼치기를 직접 눌러본 적이 없다면(저장된 값이 없으면) — 사이드바를 기본으로
  // 접어서 시작한다. 폰에서는 200px 사이드바가 화면 폭의 절반 이상을 차지해 새로
  // 만든 미팅룸 예약 같은 화면이 너무 좁아지는 문제가 있었음. 한 번이라도 직접
  // 접기/펼치기를 누르면 그 선택이 저장되어 이후에는 그 값을 그대로 쓴다.
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSE_KEY);
    if (stored !== null) return stored === '1';
    return typeof window !== 'undefined' && window.innerWidth < 640;
  });
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  };

  // 계정에 저장 — 다른 기기/브라우저에서 로그인해도 그대로 유지됨
  const toggleLang = () => setLanguage(lang === 'ko' ? 'en' : 'ko');
  const user   = JSON.parse(localStorage.getItem('user') || '{}');
  const church = user?.church;

  // 지원 모드 — 슈퍼관리자가 이 교회 admin 권한으로 임시 접속 중일 때 상단에 배너를 띄우고,
  // 로그아웃 없이 원래의 슈퍼관리자 세션으로 바로 돌아갈 수 있게 한다.
  // 지원 모드 중에 사이드바의 일반 "로그아웃"을 눌러도, 실제로는 슈퍼관리자로 돌아가려는
  // 의도일 가능성이 높으므로 exitSupportMode와 동일하게 동작시킨다.
  const isSupportMode = !!user?.support_mode;
  const logout = () => {
    if (isSupportMode) { exitSupportMode(); return; }
    localStorage.clear(); navigate('/login');
  };
  const exitSupportMode = () => {
    const retToken = localStorage.getItem('superadmin_return_token');
    const retUser  = localStorage.getItem('superadmin_return_user');
    localStorage.removeItem('superadmin_return_token');
    localStorage.removeItem('superadmin_return_user');
    localStorage.removeItem('halak_menu_perms');
    if (retToken && retUser) {
      localStorage.setItem('token', retToken);
      localStorage.setItem('access_token', retToken);
      localStorage.setItem('user', retUser);
      navigate('/super-admin');
    } else {
      // 원래 세션 정보가 없으면(다른 탭/기기 등) 안전하게 그냥 로그아웃시킨다.
      localStorage.clear();
      navigate('/login');
    }
  };

  const [logoOk, setLogoOk] = useState(true);

  const [menuPerms, setMenuPerms] = useState(() => {
    try { return JSON.parse(localStorage.getItem('halak_menu_perms') || 'null'); }
    catch { return null; }
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) return;
    axios.get('/api/menu-permissions', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setMenuPerms(res.data.data);
        localStorage.setItem('halak_menu_perms', JSON.stringify(res.data.data));
      })
      .catch(() => { /* 조회 실패 시 기존 static roles 폴백으로 렌더링됨 */ });
  }, []);

  // 메뉴 노출 여부 판단: key가 있는 항목은 서버에서 받은 설정(menuPerms) 우선,
  // 아직 못 받아왔으면 static roles로 폴백. key 없는 항목(설정/사용자관리)은 항상 static roles.
  // 'finance'(헌금/재정 담당)와 'deacon'(안수집사)/'jikje'(제직)은 사역자(pastor) 그룹과
  // 완전히 분리해서 헌금/재정(+회의록) 화면만 맡기려고 만든 전용 계정이라, 다른 메뉴
  // (교인관리/출석 등)는 roles 지정이 없어도 절대 보이면 안 된다 — 그래서 다른 항목보다
  // 먼저 확인해 예외 처리한다.
  const isMenuVisible = (item) => {
    // 2026-09: 재정 진영 세부 역할(안수집사/제직/헌금·재정담당) 겸임 기능(migrations/090)
    // — 겸임 중인 역할 중 하나라도 deacon/jikje가 있으면(주 역할이든 겸임이든) 더 넓은
    // 메뉴(재정+회의록+제직회)를 보여준다. 이 분기를 finance 단독 분기보다 먼저 확인해야
    // "주 역할은 finance인데 안수집사/제직도 겸임" 같은 계정이 회의록/제직회를 놓치지
    // 않는다(가장 넓은 권한을 우선한다는 원칙).
    if (hasRole(user, 'deacon') || hasRole(user, 'jikje')) {
      // 재정 화면(탭별로 실제 노출 여부는 화면 안에서 finance_tab_permissions로 다시
      // 걸러짐)과 회의록(회의 종류별 접근 수준은 meeting_type_permissions로 다시
      // 걸러짐) 둘 다 메뉴에는 보인다. 제직회는 관리자 승인 없이 모든 부서를 자유롭게
      // 보고 쓸 수 있는 화면이라 meetingMinutes처럼 세부 권한 테이블을 따로 두지 않음
      // — routes/committee.js도 동일하게 deacon/jikje를 포함.
      return item.key === 'finance' || item.key === 'meetingMinutes' || item.key === 'committee';
    }
    // finance(헌금/재정 담당)만 있고 deacon/jikje 겸임이 없으면 재정 화면만.
    if (user?.role === 'finance') return item.key === 'finance';

    // deacon_admin(재정 관리자)/jikje_admin(집사 관리자 — deacon_admin과 완전히 동등한
    // 재정 진영 관리자, 2026-09 추가)은 재정 메뉴는 항상 보이고(서버도 항상 허용), 그
    // 외에는 roles에 자기 role이 명시된 항목("사용자 관리", "회의록")만 본다 — 나머지
    // 사역 메뉴는 숨김.
    if (user?.role === 'deacon_admin' || user?.role === 'jikje_admin') {
      if (item.key === 'finance') return true;
      return !!item.roles?.includes(user.role);
    }

    // 헌금/재정 메뉴는 사역(pastoral) 진영에게는 — 사역 관리자(pastoral_admin) 포함해서
    // — 어떤 설정으로도 절대 보이지 않는다(사역-재정 완전 분리 원칙: 사역 관리자는
    // 곧 담임목사님 계정이므로, 여기서 열어주면 분리 자체가 무의미해진다). menuPerms에
    // 남아있는 값이 있더라도(캐시/구버전) 참고하지 않고 여기서 먼저 확실히 막는다.
    // deacon_admin/finance/deacon(위에서 이미 처리)만 이 메뉴를 볼 수 있다.
    if (item.key === 'finance') return false;

    // pastoral_admin(사역 관리자)은 예전 admin과 동일하게 항상 전 메뉴가 보인다.
    if (user?.role === 'pastoral_admin') return true;

    if (item.key && menuPerms?.[item.key]) return !!menuPerms[item.key][user?.role];
    return !item.roles || item.roles.includes(user?.role);
  };

  const SIDEBAR_WIDTH = collapsed ? 56 : 200;

  return (
    <>
    {isSupportMode && (
      <div style={{ position:'fixed', top:0, left:0, right:0, zIndex:2000, height:36,
                    background:'#F5A623', color:'#3a2a00', fontSize:13, fontWeight:600,
                    display:'flex', alignItems:'center', justifyContent:'center', gap:14 }}>
        <span>⚠ {lang === 'ko'
          ? `${church?.name_ko || ''} — 지원 모드로 접속 중입니다`
          : `Support mode — viewing as ${church?.name_ko || ''}`}</span>
        <button onClick={exitSupportMode}
          style={{ fontSize:12, padding:'3px 12px', border:'1px solid #3a2a00', borderRadius:4,
                   background:'transparent', color:'#3a2a00', cursor:'pointer', fontWeight:600 }}>
          {lang === 'ko' ? '슈퍼관리자로 돌아가기' : 'Return to Super Admin'}
        </button>
      </div>
    )}
    <div style={{ minHeight:'100vh', background:'#f9f9f9', display:'flex',
                  paddingTop: isSupportMode ? 36 : 0 }}>

      {/* 사이드바 */}
      <div style={{ width:SIDEBAR_WIDTH, background:'#534AB7', minHeight:'100vh',
                    flexShrink:0, display:'flex', flexDirection:'column',
                    position:'sticky', top:0, height:'100vh',
                    transition:'width 0.15s', overflow:'hidden' }}>

        {/* 교회명 + 접기 버튼 */}
        <div style={{ padding: collapsed ? '1.1rem 0.5rem' : '1.5rem 1.25rem',
                      borderBottom:'1px solid rgba(255,255,255,0.1)',
                      display:'flex', alignItems:'center',
                      justifyContent: collapsed ? 'center' : 'space-between' }}>
          {!collapsed && (
            <div onClick={() => navigate('/')} style={{ cursor:'pointer', minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:600, color:'#fff', lineHeight:1.3,
                            wordBreak:'break-word' }}>
                {church?.name_ko || 'Halak'}
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:4 }}>
                Church Management
              </div>
            </div>
          )}
          <button onClick={toggleCollapsed}
            title={collapsed ? (lang === 'ko' ? '펼치기' : 'Expand') : (lang === 'ko' ? '접기' : 'Collapse')}
            style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:6,
                     color:'#fff', width:28, height:28, flexShrink:0, cursor:'pointer',
                     display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>
            {collapsed ? '»' : '«'}
          </button>
        </div>

        {/* 메뉴 */}
        <nav style={{ padding:'0.75rem 0', flex:1, overflowY:'auto' }}>
          {(() => {
            const visible = NAV_ITEMS.filter(isMenuVisible);
            return visible.map((item, idx) => {
              const active = location.pathname.startsWith(item.path);
              const showGroupLabel = !collapsed && item.group && item.group !== visible[idx - 1]?.group;
              // plan에 따라 메뉴 자체는 보여주되(존재를 알려 업그레이드 유도) 비활성화하는 항목.
              // super_admin 로그인은 church가 없어(church===null) 항상 정상 노출.
              const gatedPlans = PLAN_GATED_MENU_KEYS[item.key];
              const isLocked = !!gatedPlans && !!church && !gatedPlans.includes(church.plan);
              return (
                <React.Fragment key={item.path}>
                  {showGroupLabel && (
                    <div style={{ padding: idx === 0 ? '2px 1.25rem 6px' : '16px 1.25rem 6px',
                                  fontSize:10, fontWeight:700, letterSpacing:'0.08em',
                                  textTransform:'uppercase', color:'rgba(255,255,255,0.35)' }}>
                      {lang === 'ko' ? item.group : item.groupEn}
                    </div>
                  )}
                  <div onClick={() => { if (!isLocked) navigate(item.path); }}
                    title={
                      isLocked
                        ? (lang === 'ko' ? '상위 플랜에서 이용 가능합니다.' : 'Available on a higher plan.')
                        : (collapsed ? (lang === 'ko' ? item.ko : item.en) : undefined)
                    }
                    style={{
                      padding: collapsed ? '10px 0' : '10px 1.25rem',
                      cursor: isLocked ? 'not-allowed' : 'pointer', fontSize:13,
                      display:'flex', alignItems:'center', gap:10,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderLeft: active ? '3px solid #fff' : '3px solid transparent',
                      background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                      color: isLocked ? 'rgba(255,255,255,0.32)' : (active ? '#fff' : 'rgba(255,255,255,0.75)'),
                      fontWeight: active ? 600 : 400,
                      transition:'all 0.15s',
                    }}
                    onMouseEnter={e => { if (!active && !isLocked) { e.currentTarget.style.background='rgba(255,255,255,0.08)'; e.currentTarget.style.color='#fff'; }}}
                    onMouseLeave={e => { if (!active && !isLocked) { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='rgba(255,255,255,0.75)'; }}}>
                    <i className={`ti ${item.icon}`} style={{ fontSize:16, flexShrink:0 }} />
                    {!collapsed && (
                      <>
                        {lang === 'ko' ? item.ko : item.en}
                        {isLocked && <span style={{ marginLeft:4 }}>(TBD)</span>}
                        {isLocked && <i className="ti ti-lock" style={{ fontSize:12, marginLeft:'auto', flexShrink:0 }} />}
                      </>
                    )}
                  </div>
                </React.Fragment>
              );
            });
          })()}
        </nav>

        {/* 하단 버튼 */}
        <div style={{ padding: collapsed ? '0.75rem 0.5rem' : '1rem 1.25rem',
                      borderTop:'1px solid rgba(255,255,255,0.1)' }}>
          {!collapsed && (
            <>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12, height:24 }}>
                {logoOk ? (
                  <img src="/logo-sov.png" alt="Sense of Value Studio" onError={() => setLogoOk(false)}
                    style={{ height:44, maxWidth:'80%', objectFit:'contain' }} />
                ) : (
                  <span style={{ fontSize:10, color:'rgba(255,255,255,0.3)', letterSpacing:'0.04em' }}>
                    Sense of Value Studio
                  </span>
                )}
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:8, textAlign:'center' }}>
                {user?.name_ko || user?.email || ''}
              </div>
            </>
          )}
          <button onClick={toggleLang} title={collapsed ? (lang === 'ko' ? 'EN' : '한국어') : undefined}
            style={{ width:'100%', marginBottom:6, padding:'6px', background:'rgba(255,255,255,0.1)',
                     border:'none', borderRadius:6, color:'#fff', fontSize:12, cursor:'pointer' }}>
            {collapsed ? '🌐' : (lang === 'ko' ? 'EN' : '한국어')}
          </button>
          <button onClick={logout} title={collapsed ? (lang === 'ko' ? '로그아웃' : 'Logout') : undefined}
            style={{ width:'100%', padding:'6px', background:'rgba(255,255,255,0.1)',
                     border:'none', borderRadius:6, color:'rgba(255,255,255,0.7)',
                     fontSize:12, cursor:'pointer' }}>
            {collapsed ? '⏻' : (lang === 'ko' ? '로그아웃' : 'Logout')}
          </button>
        </div>
      </div>

      {/* 메인 콘텐츠 — overflowY를 따로 걸지 않고 창 스크롤을 그대로 사용.
          (이 div에 overflow:auto가 있으면 실제 스크롤은 창에서 일어나는데
           이 div가 sticky의 기준 컨테이너로 잡혀버려서, 하위 페이지의
           position:sticky 요소들이 고정되지 않는 문제가 있었음) */}
      <div style={{ flex:1, minHeight:'100vh' }}>
        {children}
      </div>

    </div>
    <HelpPanel />
    </>
  );
}
